import {
	ChevronLeft,
	ChevronRight,
	Pause,
	Play,
	RotateCcw,
} from "lucide-react";
import {
	fractionOf,
	frameAtFraction,
	playState,
	stepDuration,
	stepStart,
} from "@/engine/playback";
import { fr } from "@/i18n/fr";
import { selectStep, selectStepCount, useViewerStore } from "@/state/store";

const stripPrefix = (title: string) => title.replace(/^Étape \d+ · /, "");

/** Playback speeds, cycled by the speed button. */
const SPEEDS = [0.5, 0.75, 1, 1.5] as const;
const nextSpeed = (speed: number): number =>
	SPEEDS[
		(SPEEDS.indexOf(speed as (typeof SPEEDS)[number]) + 1) % SPEEDS.length
	] ?? 1;
const formatSpeed = (speed: number): string =>
	`${String(speed).replace(".", ",")}×`;

export function StepCard() {
	const step = useViewerStore(selectStep);
	const total = useViewerStore(selectStepCount);
	const data = useViewerStore((s) => s.data);
	const frame = useViewerStore((s) => s.frame);
	const playing = useViewerStore((s) => s.playing);
	const speed = useViewerStore((s) => s.speed);
	const stepIndex = useViewerStore((s) => s.stepIndex);
	const { goStep, setFrame, setPlaying, setSpeed } = useViewerStore.getState();
	if (!step || !data) return null;

	const state = playState(step, frame, playing);
	const fps = data.product.fps;
	const eyebrow =
		step.num === 0
			? fr.intro
			: step.num > total
				? fr.done
				: fr.stepOf(step.num, total);
	const playLabel = playing
		? fr.pause
		: state === "done"
			? fr.replay
			: state === "paused"
				? fr.resume
				: fr.play;
	const PlayIcon = playing ? Pause : state === "done" ? RotateCcw : Play;
	const showClick = step.click && playing && fractionOf(step, frame) > 0.82;

	const togglePlay = () => {
		if (!playing && frame >= step.f1) setFrame(stepStart(step));
		setPlaying(!playing);
	};

	return (
		<div className="card">
			<div className="eyebrow">
				<span>{eyebrow}</span>
				<span className={`state ${state === "done" ? "done" : ""}`}>
					{fr.state[state]}
				</span>
				{showClick && <span className="click">{fr.click}</span>}
			</div>
			<h2>{stripPrefix(step.title)}</h2>
			<p>{step.detail}</p>
			<div className="controls">
				<button
					className="btn icon"
					title={fr.prev}
					aria-label={fr.prev}
					type="button"
					disabled={stepIndex === 0}
					onClick={() => goStep(stepIndex - 1)}
				>
					<ChevronLeft aria-hidden="true" />
				</button>
				<button className="btn primary" type="button" onClick={togglePlay}>
					<PlayIcon
						aria-hidden="true"
						className={playing || state === "done" ? "" : "fill-current"}
					/>
					{playLabel}
				</button>
				<button
					className="btn icon"
					title={fr.next}
					aria-label={fr.next}
					type="button"
					disabled={stepIndex === data.steps.length - 1}
					onClick={() => goStep(stepIndex + 1)}
				>
					<ChevronRight aria-hidden="true" />
				</button>
				<button
					className="btn speed"
					type="button"
					title={fr.speed}
					aria-label={`${fr.speed} : ${formatSpeed(speed)}`}
					onClick={() => setSpeed(nextSpeed(speed))}
				>
					{formatSpeed(speed)}
				</button>
				<div className="scrub">
					<input
						type="range"
						min={0}
						max={1000}
						value={Math.round(1000 * fractionOf(step, frame))}
						aria-label={fr.scrub}
						onChange={(e) => {
							setPlaying(false);
							setFrame(frameAtFraction(step, Number(e.target.value) / 1000));
						}}
					/>
					<span>
						{fr.seconds((frame - stepStart(step)) / fps)} /{" "}
						{fr.seconds(stepDuration(step, fps))}
					</span>
				</div>
			</div>
		</div>
	);
}
